import React, { useEffect, useState, memo, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import authService from '../../services/authService';

const Sidebar = memo(({ isOpen, toggleSidebar }) => {
  const location = useLocation();
  const isAdmin = authService.isAdmin();
  const isWorkerOrHigher = authService.isWorkerOrHigher();

  // FIX #11: Persist sidebar state to localStorage
  const [tripsDropdownOpen, setTripsDropdownOpen] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar_trips_dropdown_open');
      return saved === 'true';
    } catch {
      return false;
    }
  });

  const [vehiclesDropdownOpen, setVehiclesDropdownOpen] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar_vehicles_dropdown_open');
      return saved === 'true';
    } catch {
      return false;
    }
  });

  const [tyresDropdownOpen, setTyresDropdownOpen] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar_tyres_dropdown_open');
      return saved === 'true';
    } catch {
      return false;
    }
  });

  // Close sidebar on mobile when route changes
  useEffect(() => {
    if (window.innerWidth < 1024 && isOpen) {
      toggleSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const isActive = useCallback((path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  }, [location.pathname]);

  // Check if trips section is active
  const isTripsActive = useCallback(() => {
    return location.pathname.startsWith('/trips') ||
           location.pathname.includes('/trips/');
  }, [location.pathname]);

  // Check if vehicles section is active
  const isVehiclesActive = useCallback(() => {
    return location.pathname.startsWith('/vehicles') ||
           location.pathname.includes('/vehicles/');
  }, [location.pathname]);

  // Check if tyres section is active
  const isTyresActive = useCallback(() => {
    return location.pathname.startsWith('/tyres') ||
           location.pathname.includes('/tyres/');
  }, [location.pathname]);

  // OPTIMIZED: Auto-open dropdowns based on current route (consolidated into single useEffect)
  useEffect(() => {
    const updates = {};
    let hasUpdates = false;

    if (isTripsActive() && !tripsDropdownOpen) {
      updates.trips = true;
      hasUpdates = true;
    }
    if (isVehiclesActive() && !vehiclesDropdownOpen) {
      updates.vehicles = true;
      hasUpdates = true;
    }
    if (isTyresActive() && !tyresDropdownOpen) {
      updates.tyres = true;
      hasUpdates = true;
    }

    // Only update if there are actual changes to prevent unnecessary re-renders
    if (hasUpdates) {
      if (updates.trips) setTripsDropdownOpen(true);
      if (updates.vehicles) setVehiclesDropdownOpen(true);
      if (updates.tyres) setTyresDropdownOpen(true);
    }
  }, [location.pathname, tripsDropdownOpen, vehiclesDropdownOpen, tyresDropdownOpen, isTripsActive, isVehiclesActive, isTyresActive]);

  // OPTIMIZED: Save all dropdown states to localStorage (consolidated into single useEffect)
  useEffect(() => {
    try {
      localStorage.setItem('sidebar_trips_dropdown_open', tripsDropdownOpen.toString());
      localStorage.setItem('sidebar_vehicles_dropdown_open', vehiclesDropdownOpen.toString());
      localStorage.setItem('sidebar_tyres_dropdown_open', tyresDropdownOpen.toString());
    } catch (err) {
      // Ignore localStorage errors (e.g., in private browsing)
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('Failed to save sidebar state:', err);
      }
    }
  }, [tripsDropdownOpen, vehiclesDropdownOpen, tyresDropdownOpen]);

  // OPTIMIZED: Memoize menu items to prevent recreating on every render
  const menuItems = useMemo(() => [
    {
      name: 'Resoles',
      path: '/resoles',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      show: true,
    },
    {
      name: 'Maintenance',
      path: '/maintenance',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
        </svg>
      ),
      show: true,
    },
    {
      name: 'Maintenance Rules',
      path: '/maintenance-rules',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      show: isAdmin,
    },
    {
      name: 'Notifications',
      path: '/maintenance-notifications',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
      show: true,
    },
    {
      name: 'Reports',
      path: '/reports/tyres',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      show: true,
    },
    {
      name: 'My Requests',
      path: '/my-requests',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      show: isWorkerOrHigher,
    },
  ], [isAdmin, isWorkerOrHigher]);

  // OPTIMIZED: Memoize admin menu items
  const adminMenuItems = useMemo(() => [
    {
      name: 'User Management',
      path: '/users',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      show: isAdmin,
    },
    {
      name: 'Edit Approvals',
      path: '/approvals',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      show: isAdmin,
    },
  ], [isAdmin]);

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
          onClick={toggleSidebar}
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-30 h-screen bg-gray-900 text-white transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } w-64`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 bg-gray-800 border-b border-gray-700">
            <Link to="/dashboard" className="flex items-center" onClick={() => toggleSidebar()}>
              <span className="text-xl font-bold text-red-500">Ayyoob and Co.</span>
            </Link>
            {/* Close button for mobile */}
            <button
              onClick={toggleSidebar}
              className="lg:hidden text-gray-400 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 overflow-y-auto">
            {/* Main Menu */}
            <div className="space-y-1">
              <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Main Menu
              </p>

              {/* Dashboard */}
              <Link
                to="/dashboard"
                onClick={() => toggleSidebar()}
                className={`flex items-center px-3 py-3 rounded-lg transition-colors duration-200 ${
                  isActive('/dashboard')
                    ? 'bg-red-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <span className={isActive('/dashboard') ? 'text-white' : 'text-gray-400'}>
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </span>
                <span className="ml-3 font-medium">Dashboard</span>
              </Link>

              {/* Vehicles Dropdown Menu */}
              <div>
                <button
                  onClick={() => setVehiclesDropdownOpen(!vehiclesDropdownOpen)}
                  className={`w-full flex items-center justify-between px-3 py-3 rounded-lg transition-colors duration-200 ${
                    isVehiclesActive()
                      ? 'bg-red-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <span className={isVehiclesActive() ? 'text-white' : 'text-gray-400'}>
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </span>
                    <span className="ml-3 font-medium">Vehicles</span>
                  </div>
                  <svg
                    className={`w-4 h-4 transition-transform ${vehiclesDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Items */}
                {vehiclesDropdownOpen && (
                  <div className="ml-6 mt-1 space-y-1">
                    <Link
                      to="/vehicles"
                      onClick={() => toggleSidebar()}
                      className={`flex items-center px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${
                        location.pathname === '/vehicles'
                          ? 'bg-red-500 text-white'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`}
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      All Vehicles
                    </Link>
                    <Link
                      to="/vehicles/create"
                      onClick={() => toggleSidebar()}
                      className={`flex items-center px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${
                        location.pathname === '/vehicles/create'
                          ? 'bg-red-500 text-white'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`}
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add New
                    </Link>
                  </div>
                )}
              </div>

              {/* Tyres Dropdown Menu */}
              <div>
                <button
                  onClick={() => setTyresDropdownOpen(!tyresDropdownOpen)}
                  className={`w-full flex items-center justify-between px-3 py-3 rounded-lg transition-colors duration-200 ${
                    isTyresActive()
                      ? 'bg-red-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <span className={isTyresActive() ? 'text-white' : 'text-gray-400'}>
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10h.01M15 10h.01M9.5 15.5a3.5 3.5 0 005 0" />
                      </svg>
                    </span>
                    <span className="ml-3 font-medium">Tyres</span>
                  </div>
                  <svg
                    className={`w-4 h-4 transition-transform ${tyresDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Items */}
                {tyresDropdownOpen && (
                  <div className="ml-6 mt-1 space-y-1">
                    <Link
                      to="/tyres"
                      onClick={() => toggleSidebar()}
                      className={`flex items-center px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${
                        location.pathname === '/tyres'
                          ? 'bg-red-500 text-white'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`}
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      All Tyres
                    </Link>
                    <Link
                      to="/tyres/create"
                      onClick={() => toggleSidebar()}
                      className={`flex items-center px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${
                        location.pathname === '/tyres/create'
                          ? 'bg-red-500 text-white'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`}
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add New
                    </Link>
                  </div>
                )}
              </div>

              {/* Trips Dropdown Menu */}
              <div>
                <button
                  onClick={() => setTripsDropdownOpen(!tripsDropdownOpen)}
                  className={`w-full flex items-center justify-between px-3 py-3 rounded-lg transition-colors duration-200 ${
                    isTripsActive()
                      ? 'bg-red-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    <span className={isTripsActive() ? 'text-white' : 'text-gray-400'}>
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                    </span>
                    <span className="ml-3 font-medium">Trips</span>
                  </div>
                  <svg
                    className={`w-4 h-4 transition-transform ${tripsDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Items */}
                {tripsDropdownOpen && (
                  <div className="ml-6 mt-1 space-y-1">
                    <Link
                      to="/trips/all"
                      onClick={() => toggleSidebar()}
                      className={`flex items-center px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${
                        location.pathname === '/trips/all'
                          ? 'bg-red-500 text-white'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`}
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      All Trips
                    </Link>
                    <Link
                      to="/trips/create"
                      onClick={() => toggleSidebar()}
                      className={`flex items-center px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${
                        location.pathname === '/trips/create' || location.pathname === '/trips'
                          ? 'bg-red-500 text-white'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`}
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Record Trip
                    </Link>
                  </div>
                )}
              </div>

              {/* Other Menu Items */}
              {menuItems.map((item) =>
                item.show ? (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => toggleSidebar()}
                    className={`flex items-center px-3 py-3 rounded-lg transition-colors duration-200 ${
                      isActive(item.path)
                        ? 'bg-red-600 text-white'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <span className={isActive(item.path) ? 'text-white' : 'text-gray-400'}>
                      {item.icon}
                    </span>
                    <span className="ml-3 font-medium">{item.name}</span>
                  </Link>
                ) : null
              )}
            </div>

            {/* Admin Menu */}
            {isAdmin && (
              <div className="mt-8 space-y-1">
                <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Administration
                </p>
                {adminMenuItems.map((item) =>
                  item.show ? (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => toggleSidebar()}
                      className={`flex items-center px-3 py-3 rounded-lg transition-colors duration-200 ${
                        isActive(item.path)
                          ? 'bg-red-600 text-white'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`}
                    >
                      <span className={isActive(item.path) ? 'text-white' : 'text-gray-400'}>
                        {item.icon}
                      </span>
                      <span className="ml-3 font-medium">{item.name}</span>
                    </Link>
                  ) : null
                )}
              </div>
            )}

            {/* Quick Actions */}
            {isWorkerOrHigher && (
              <div className="mt-8 space-y-2">
                <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Quick Actions
                </p>
                <Link
                  to="/vehicles/create"
                  onClick={() => toggleSidebar()}
                  className="flex items-center px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors duration-200"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Vehicle
                </Link>
                <Link
                  to="/tyres/create"
                  onClick={() => toggleSidebar()}
                  className="flex items-center px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors duration-200"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Tyre
                </Link>
                <Link
                  to="/trips/create"
                  onClick={() => toggleSidebar()}
                  className="flex items-center px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors duration-200"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Record Trip
                </Link>
              </div>
            )}
          </nav>

          {/* Footer - System Status */}
          <div className="px-4 py-4 bg-gray-800 border-t border-gray-700">
            <div className="flex items-center text-sm">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="ml-2 text-gray-400">System Online</span>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;
